import { useState, useEffect } from "react";
import apiRequest from "../../../../../utils/ApiRequest/apiRequest.js";
import Form from "../../../../Reusable/Form/Form.js";
import config from "config";
import RecoverPasswordModal from "./Modals/RecoverPasswordModal.js";
import AccountDetails from "./AccountDetails.js";
import ProfilePicture from "./ProfilePicture.js";
import DeleteUserModal from "./Modals/DeleteUserModal.js";
import ToastHtmlComponent from "../../../../Reusable/ToastHtmlComponent/ToastHtmlComponent.js";
import GlobalPopUp from "../../../../Reusable/GlobalPopUp/GlobalPopUp.js";
import useUpdatePasswordRequirements from "hooks/useUpdatePasswordRequirements.ts";
import { useContent } from "../../../../../contexts/contentContext/contentContext.js";
import { PasswordFields, MyProfileFields } from "./MyProfileFields.js";
import { useUser } from "../../../../../contexts/userContext/userContext.js";
import { useToasts } from "react-toast-notifications";
import { useModal } from "../../../../../contexts/modalContext/modalContext.js";
import { useCookie } from "contexts/cookieContext/cookieContext.js";
import { ButtonAccessability } from "components/Elements/Button/Button.js";

import "../Profile.scss";

const MyProfile = ({ selectedFileId }) => {
  const { getTextByKey, getLanguageCulture } = useContent();
  const { setModal } = useModal();
  const { getUserData } = useUser();
  const userData = getUserData();
  const { addToast } = useToasts();
  const [countries, setCountries] = useState([]);
  const [occupations, setOccupations] = useState([]);
  const [formFields, setFormFields] = useState(MyProfileFields);
  const { setCookies } = useCookie();

  //Get countries and occupations
  useEffect(() => {
    getCountries();
    getOccupations();
  }, []);

  //get The Code And Name of user's country
  useEffect(() => {
    if (userData) {
      if (userData.ExternalLoginNoPass) {
        setFormFields((prev) => ({
          ...prev,
          UserEmail: {
            ...prev.UserEmail,
            tooltip: {
              text: "tooltip-user-external-registration",
            },
            disabled: true,
          },
        }));
      }
    }
  }, [userData]);

  const passFields = useUpdatePasswordRequirements({
    defaultFields: PasswordFields,
    fieldName: "UserNewPassword",
    confirmField: "ConfirmPassword",
  });

  //Get all occupations list
  const getOccupations = async () => {
    const parameters = {
      occupationCulture: getLanguageCulture(),
      useCache: true,
    };
    const response = await apiRequest(
      "Dictionaries",
      "GetReactOccupations",
      parameters
    );
    if (response.status === "success") {
      const correctArray = response.data.map((item) => {
        return {
          constKey: item.title,
          value: item.system_name,
        };
      });
      correctArray.unshift({
        constKey: getTextByKey("profileuserdetails-select-occupation"),
        value: "-1",
      });
      setOccupations(correctArray);
      setFormFields((prev) => {
        return {
          ...prev,
          UserOccupation: {
            ...prev.UserOccupation,
            selectList: correctArray,
          },
          UserEmail: {
            ...prev.UserEmail,
            tooltip: getUserData()?.IsSubUser
              ? {
                  text: "tooltip-sub-user-email-change-disabled",
                }
              : undefined,
            disabled: getUserData()?.IsSubUser,
          },
        };
      });
    }
  };

  //Get all Countries list
  const getCountries = async () => {
    const parameters = {
      culture: getLanguageCulture(),
    };
    const response = await apiRequest(
      "Dictionaries",
      "GetCountries",
      parameters
    );
    if (response.status === "success") {
      setFormFields((prev) => {
        const correctArray = response.data.map((item) => {
          return {
            constKey: item.countryKey,
            value: item.countryValue,
          };
        });
        setCountries(correctArray);
        return {
          ...prev,
          TypeSelectList: {
            ...prev.TypeSelectList,
            selectList: correctArray,
          },
        };
      });
    }
  };

  //Update Password - just need to fix the error with uncurrent password check
  const handlePasswordFormSubmit = async (body) => {
    if (body.UserNewPassword) {
      if (body.UserNewPassword.length < 5) {
        return {
          status: "error",
          message: "profileuserdetails-password-length-min",
        };
      } else if (body.UserNewPassword.length > 50) {
        return {
          status: "error",
          message: "profileuserdetails-password-length-max",
        };
      } else if (body.UserNewPassword !== body.ConfirmPassword) {
        return {
          status: "error",
          message: "profileuserdetails-no-match",
        };
      } else {
        const res = await checkPassword(body);
        return res;
      }
    }
    return { status: "success", data: null };
  };

  const updatePassword = async (body) => {
    const newBody = {
      ...body,
      Culture: getLanguageCulture(),
    };
    const response = await apiRequest(
      "Users",
      "ReactResetUserPassword",
      newBody,
      "POST"
    );
    if (response.status === "success" && response.data === true) {
      addToast(getTextByKey("update-details-success"), {
        appearance: "success",
      });

      setCookies("loginDate", new Date().getTime(), {
        path: "/",
        domain: ".jumbomail.me",
      });
    } else {
      let errText = getTextByKey("api-error");
      errText = errText.replace("#ERRORNUM#", 999);
      errText = errText.replace("#SYSMAIL#", "contact-us");
      errText = errText.replace("#SYSMAIL#", config.supportmail);
      errText = errText.replace("#SYSMAIL#", "contact-us");
      addToast(<ToastHtmlComponent htmlStr={errText} />, {
        appearance: "error",
      });
    }
    return response;
  };

  //check password validation to change mail
  const checkPassword = async (body) => {
    const parameters = {
      password: body.UserCurrentPassword,
    };

    const response = await apiRequest("Users", "CheckUserPassword", parameters);
    if (response.status === "success") {
      if (response.data === 6) {
        return {
          status: "error",
          message: getTextByKey("profileuserdetails-Wrong-password"),
        };
      } else if (response.data === 0) {
        return await updatePassword(body);
      } else {
        let errText = getTextByKey("api-error");
        errText = errText.replace("#ERRORNUM#", response.data);
        errText = errText.replace("#SYSMAIL#", "contact-us");
        errText = errText.replace("#SYSMAIL#", config.supportmail);
        errText = errText.replace("#SYSMAIL#", "contact-us");
        addToast(<ToastHtmlComponent htmlStr={errText} />, {
          appearance: "error",
        });
      }
    } else {
      let errText = getTextByKey("api-error");
      errText = errText.replace("#ERRORNUM#", response.data);
      errText = errText.replace("#SYSMAIL#", "contact-us");
      errText = errText.replace("#SYSMAIL#", config.supportmail);
      errText = errText.replace("#SYSMAIL#", "contact-us");
      addToast(<ToastHtmlComponent htmlStr={errText} />, {
        appearance: "error",
      });
    }
  };

  const handleOpenDeleteAccount = () => {
    if (userData.IsSubscribed) {
      setModal(() => <GlobalPopUp text={"user-subscriber-cant-delete"} />);
    } else {
      setModal(() => <DeleteUserModal userData={userData} />);
    }
  };

  return (
    <div className="main-section-myprofile">
      {userData && countries.length > 0 && occupations.length > 0 ? (
        <>
          <h1>{getTextByKey("profileuserdetails-title")}</h1>
          <div className="content-section">
            {window.screen.width > 600 ? (
              <AccountDetails
                countries={countries}
                occupations={occupations}
                formFields={formFields}
                userData={userData}
              />
            ) : null}
            <div className="spacer"></div>
            <div className="right-area">
              <ProfilePicture
                selectedFileId={selectedFileId}
                userData={userData}
              />
              {window.screen.width <= 600 ? (
                <AccountDetails
                  countries={countries}
                  occupations={occupations}
                  formFields={formFields}
                  userData={userData}
                />
              ) : null}
              <div className="bottom-section">
                {userData.ExternalLoginNoPass ? (
                  <div className="no-password-section">
                    <div className="no-pass-title">
                      {getTextByKey("profileuserdetails-no-password-title")}
                    </div>
                    <div className="no-pass-text">
                      {getTextByKey(
                        "profileuserdetails-no-password-text"
                      ).replace("#service#", userData.ExternalLoginNoPass)}
                    </div>
                    <ButtonAccessability buttonClassName="no-pass-button">
                      {getTextByKey(
                        "profileuserdetails-no-password-button"
                      ).replace("#userEmail#", userData.UserName)}
                    </ButtonAccessability>
                  </div>
                ) : (
                  <>
                    <div className="password-row">
                      <h2>
                        {getTextByKey(
                          "profileuserdetails-change-password-title"
                        )}
                      </h2>
                      <div className="forgot-password">
                        <div
                          className="delete-account-text"
                          onClick={() => {
                            setModal(() => (
                              <RecoverPasswordModal userData={userData} />
                            ));
                          }}
                        >
                          {getTextByKey("profileuserdetails-forgot-password")}
                        </div>
                      </div>
                    </div>
                    <Form
                      fieldsProps={{ field: "square" }}
                      buttonProps={{ buttonsSize: "small" }}
                      fields={passFields}
                      submitProps={{
                        title: getTextByKey(
                          "profileuserdetails-update-password-btn"
                        ),
                      }}
                      defaultValuesObj={{
                        UserCurrentPassword: "",
                        UserNewPassword: "",
                        ConfirmPassword: "",
                      }}
                      clearAfterSubmit={true}
                      onSubmit={handlePasswordFormSubmit}
                      // captchaProvider="cloudflare"
                      // cloudflareActionName="updatePassword"
                    ></Form>{" "}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="delete-account-container">
            <div
              className="delete-account-text"
              onClick={() => {
                handleOpenDeleteAccount();
              }}
            >
              {getTextByKey("profileuserdetails-delete-account-btn")}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default MyProfile;
